import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Heart, Palmtree } from "lucide-react";
import { ETSY_SHOP } from "../data/site";
import { MaskedLine, EASE } from "./motion";

const HERO_IMG =
  "https://images.pexels.com/photos/31352326/pexels-photo-31352326.jpeg?auto=compress&cs=tinysrgb&w=1200";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yBlobA = useTransform(scrollYProgress, [0, 1], [0, 190]);
  const yBlobB = useTransform(scrollYProgress, [0, 1], [0, -150]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-cream">
      <motion.div style={{ y: yBlobA }} className="absolute -top-24 right-[8%] h-[28rem] w-[28rem] rounded-full bg-lavender opacity-70 blur-3xl" />
      <motion.div style={{ y: yBlobB }} className="absolute bottom-[10%] left-[-6%] h-[24rem] w-[24rem] rounded-full bg-blush opacity-80 blur-3xl" />
      <div className="absolute top-[38%] right-[38%] hidden h-40 w-40 rounded-full bg-sage/50 blur-2xl lg:block" />

      <div className="relative mx-auto grid max-w-[1600px] gap-16 px-6 pt-36 pb-24 md:px-12 lg:grid-cols-12 lg:pt-44">
        <div className="lg:col-span-7">
          <MaskedLine delay={0.1}>
            <p className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.35em] text-navy/50">
              <span className="inline-block h-px w-10 bg-navy/40" />
              Dream Freely Studios — Est. for the hopeful
            </p>
          </MaskedLine>

          <h1 className="mt-10 font-playfair font-black leading-[0.85] tracking-tight text-navy">
            <MaskedLine delay={0.25} className="text-[17vw] sm:text-[13vw] lg:text-[9.5vw]">
              <span>Dream big.</span>
            </MaskedLine>
            <MaskedLine delay={0.4} className="text-[17vw] sm:text-[13vw] lg:text-[9.5vw]">
              <span className="italic font-medium">Live freely.</span>
            </MaskedLine>
          </h1>

          <MaskedLine delay={0.6}>
            <p className="mt-6 -rotate-2 font-caveat text-5xl font-semibold text-mustard md:text-7xl">
              make today amazing
              <Heart className="mb-2 ml-2 inline-block h-7 w-7 fill-mustard text-mustard" />
            </p>
          </MaskedLine>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.9 }}
            className="mt-14 flex flex-wrap items-center gap-6"
          >
            <a
              href={ETSY_SHOP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-shop-etsy-btn"
              className="rounded-full bg-navy px-9 py-4 text-xs font-medium uppercase tracking-[0.25em] text-cream transition-transform duration-300 hover:scale-95"
            >
              Shop the Collection
            </a>
            <Link
              to="/travel"
              data-testid="hero-book-travel-btn"
              className="group flex items-center gap-3 rounded-full border border-navy px-9 py-4 text-xs font-medium uppercase tracking-[0.25em] text-navy transition-transform duration-300 hover:scale-95"
            >
              <Palmtree className="h-4 w-4" />
              Book Travel
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.15 }}
            className="mt-16 max-w-md border-l-2 border-mustard pl-5 font-playfair text-xl italic leading-relaxed text-navy/70 md:text-2xl"
          >
            “Good things are allowed to happen.”
          </motion.p>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, ease: EASE, delay: 0.5 }}
            className="relative ml-auto w-full max-w-md"
          >
            <div className="overflow-hidden rounded-t-[999px] border border-navy/20 bg-blush shadow-[0_40px_80px_-30px_rgba(27,42,74,0.35)]">
              <motion.img
                src={HERO_IMG}
                alt="Sunlit corner with a Dream Freely tote"
                data-testid="hero-image"
                style={{ y: yImg }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="aspect-[3/4] w-full scale-110 object-cover"
              />
            </div>
            <div className="absolute -left-12 top-8 h-28 w-28 animate-spin-slow md:h-32 md:w-32">
              <svg viewBox="0 0 100 100" className="h-full w-full">
                <defs>
                  <path id="stamp-circle" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
                </defs>
                <text className="fill-navy text-[8px] font-medium uppercase" style={{ letterSpacing: "0.22em" }}>
                  <textPath href="#stamp-circle">Dream Freely Studios · Good Things ·</textPath>
                </text>
              </svg>
              <Heart className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 fill-mustard text-mustard" />
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: EASE, delay: 1 }}
              className="absolute -bottom-8 -left-8 border border-navy/20 bg-cream px-6 py-5 shadow-[0_20px_50px_-20px_rgba(27,42,74,0.4)] md:-left-16"
            >
              <p className="font-caveat text-2xl font-semibold text-navy">Dream Freely</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-navy/50">Nine types to shop</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
